import React, { useMemo } from 'react';
import { MdClose } from 'react-icons/md';
import { CartItem } from '../../redux/slices/cartSlice';
import { formatModifiersForDisplay } from '../../utils/cartUtils';

interface DeliveryQuoteModalProps {
  isOpen: boolean;
  cartItems: CartItem[];
  deliveryFee: number;
  subtotal: number;
  total: number;
  onPlaceOrder: () => void;
  onClose: () => void;
  loading: boolean;
}

export const DeliveryQuoteModal: React.FC<DeliveryQuoteModalProps> = ({
  isOpen,
  cartItems,
  deliveryFee,
  subtotal,
  total,
  onPlaceOrder,
  onClose,
  loading,
}) => {
  // Dynamic modal sizing based on cart size - compact widths
  const modalSize = useMemo(() => {
    if (cartItems.length > 5) {
      return 'min-w-[420px] max-w-lg min-h-[600px] max-h-[85vh]';  // Large cart: 420-512px
    }
    if (cartItems.length > 3) {
      return 'min-w-[400px] max-w-md min-h-[500px] max-h-[80vh]';  // Medium cart: 400-448px
    }
    return 'min-w-[380px] max-w-sm min-h-[400px] max-h-[75vh]';    // Small cart: 380-384px
  }, [cartItems.length]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`bg-white rounded-lg shadow-2xl w-full flex flex-col overflow-hidden ${modalSize}`}>
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Delivery Quote</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <MdClose className="w-6 h-6" />
          </button>
        </div>

        {/* Order Items - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 mb-2">Order Items</h4>
            {cartItems.map((item) => (
              <div key={item.cartItemId} className="py-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">
                    {item.quantity}x {item.item?.name || 'Item'}
                  </span>
                  <span className="font-medium text-gray-900">
                    ${(item.totalPrice * item.quantity).toFixed(2)}
                  </span>
                </div>
                
                {/* Display modifiers - show all including $0.00 */}
                {formatModifiersForDisplay(item.selected.modifiers).length > 0 && (
                  <div className="mt-1 ml-12">
                    {formatModifiersForDisplay(item.selected.modifiers).map((modifier, index) => (
                      <div 
                        key={index}
                        className="flex justify-between text-xs text-gray-500"
                      >
                        <span>{modifier.options}</span>
                        <span>(${(modifier.price * item.quantity).toFixed(2)})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Pricing & Button - Sticky Footer */}
        <div className="border-t border-gray-200 bg-white">
          {/* Pricing Breakdown */}
          <div className="p-6 pb-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span className="text-gray-900">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm font-medium text-green-600">
              <span>Delivery Fee</span>
              <span>${deliveryFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
              <span className="text-gray-900">Total</span>
              <span className="text-red-500">${total.toFixed(2)}</span>
            </div>
          </div>

          {/* Place Order Button */}
          <div className="px-6 pb-6 bg-gray-50">
            <button
              onClick={onPlaceOrder}
              disabled={loading}
              className={`w-full bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors flex items-center justify-center ${
                loading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Placing Order...
                </>
              ) : (
                'Place Order'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
